import { useCallback, useEffect, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from "@chakra-ui/react";

import { FiSearch } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";

import { Paginate } from "@/components/common/table/Paginate";
import { useCompany } from "@/hooks/util/useCompany";
import {
  selectCompanyUpdating,
  updateCompany
} from "@/store/features/companies/companies";
import {
  loadSuppliers,
  selectIsLoading,
  selectSuppliers,
  selectUpdateSuppliersSuccess,
  Supplier,
  SupplierWithName,
  updateSuppliers
} from "@/store/features/lineItemExtraction/lineItemExtraction";

const pageSizeOptions = [10, 20, 50];

export const ClientLineItemExtraction = () => {
  const dispatch = useDispatch();
  const { activeClientId, company } = useCompany();
  const isCompanyUpdating = useSelector(selectCompanyUpdating);
  const updateSuppliersSuccess = useSelector(selectUpdateSuppliersSuccess);
  const isLoading = useSelector(selectIsLoading);
  const suppliers = useSelector(selectSuppliers);

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [lineItemExtractionEnabled, setLineItemExtractionEnabled] = useState<
    boolean | undefined
  >(undefined);
  const [originalSuppliers, setOriginalSuppliers] = useState<
    SupplierWithName[]
  >([]);
  const [localSuppliers, setLocalSuppliers] = useState<SupplierWithName[]>([]);
  const [updatedSuppliers, setUpdatedSuppliers] = useState<Supplier[]>([]);

  const checkForUpdatesToSuppliers = useCallback(() => {
    const suppliersWithUpdates = localSuppliers.filter(
      (supplier) =>
        supplier.is_line_items_extraction_disabled !==
        originalSuppliers.find((s) => s.id === supplier.id)
          ?.is_line_items_extraction_disabled
    );
    return suppliersWithUpdates;
  }, [localSuppliers, originalSuppliers]);

  const handleLineItemExtractionEnabledChange = useCallback(
    (value: boolean) => {
      if (!company || !activeClientId) return;
      setLineItemExtractionEnabled(value);
      dispatch(
        updateCompany(activeClientId, {
          ...company,
          is_line_items_extraction_enabled: value
        })
      );
    },
    [company, activeClientId, dispatch]
  );

  const handleSelect = useCallback((supplierId: string) => {
    setLocalSuppliers((previousState) =>
      previousState.map((supplier) =>
        supplier.id === supplierId
          ? {
              ...supplier,
              is_line_items_extraction_disabled:
                !supplier.is_line_items_extraction_disabled
            }
          : supplier
      )
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    const allEnabled = localSuppliers.every(
      (supplier) => supplier.is_line_items_extraction_disabled
    );
    setLocalSuppliers((previousState) =>
      previousState.map((supplier) => ({
        ...supplier,
        is_line_items_extraction_disabled: !allEnabled
      }))
    );
  }, [localSuppliers]);

  const handleUpdate = useCallback(() => {
    if (!activeClientId) return;
    dispatch(
      updateSuppliers({
        clientId: activeClientId,
        suppliers: updatedSuppliers
      })
    );
  }, [activeClientId, dispatch, updatedSuppliers]);

  const handlePageClick = useCallback(
    (event: { selected: number }) => {
      setPage(event.selected);
      setOffset(event.selected * limit);
    },
    [limit]
  );

  useEffect(() => {
    if (activeClientId) {
      dispatch(loadSuppliers(activeClientId, offset, limit, search));
    }
  }, [dispatch, activeClientId, offset, limit, search, updateSuppliersSuccess]);

  useEffect(() => {
    if (company) {
      setLineItemExtractionEnabled(company.is_line_items_extraction_enabled);
    }

    if (suppliers?.items) {
      setOriginalSuppliers(suppliers.items);
      setLocalSuppliers(suppliers.items);
    }
  }, [company, suppliers]);

  useEffect(() => {
    const updatedSuppliersList = checkForUpdatesToSuppliers().map(
      ({ id, is_line_items_extraction_disabled }) => ({
        id,
        is_line_items_extraction_disabled
      })
    );
    setUpdatedSuppliers(updatedSuppliersList);
  }, [localSuppliers, checkForUpdatesToSuppliers]);

  if (!company) {
    return (
      <HStack justify="center" align="center" h="100%">
        <Spinner />
      </HStack>
    );
  }

  return (
    <Box mt={8} rounded={16} px={10} py={10} bg="white">
      <Flex justify="space-between" mb={2} alignItems="flex-start" gap={8}>
        <Box>
          <Heading mb={2} fontWeight={600}>
            Line Item Extraction
          </Heading>
          <Text color="darkGray" mb={2}>
            Turning <b>OFF</b> Line Item Extraction will mean that only summary
            transaction data will be displayed for all documents extracted.
          </Text>
          <Text color="darkGray" mb={2}>
            Turning <b>ON</b> Line Item Extraction will mean all line item
            transaction data will be displayed{" "}
            <b>except for any suppliers NOT selected below</b>.
          </Text>
          {lineItemExtractionEnabled && (
            <Box>
              <Text color="darkGray" mb={2}>
                Search to add Supplier
              </Text>

              <Flex justify="center" align="center">
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FiSearch color="darkGray" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search by Supplier"
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </InputGroup>
              </Flex>
              <Divider my={2} />
              {updatedSuppliers.length > 0 && (
                <HStack mb={2} gap={6}>
                  <Text>
                    {updatedSuppliers.length} supplier
                    {updatedSuppliers.length === 1 ? "" : "s"} changed on this
                    page
                  </Text>
                  <Button
                    onClick={handleUpdate}
                    colorScheme="primary"
                    height={9}
                  >
                    Update
                  </Button>
                </HStack>
              )}
              <Box
                borderRadius={8}
                borderWidth={1}
                borderColor="gray.200"
                overflow="hidden"
              >
                <Table variant="simple" size="md">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th
                        fontWeight={700}
                        color="gray.700"
                        borderRight="1px solid"
                        borderColor="gray.200"
                      >
                        SUPPLIER
                      </Th>
                      <Th w="60px">
                        <Checkbox
                          isChecked={
                            localSuppliers.length > 0 &&
                            localSuppliers.every(
                              (supplier) =>
                                !supplier.is_line_items_extraction_disabled
                            )
                          }
                          onChange={handleSelectAll}
                        />
                      </Th>
                    </Tr>
                  </Thead>
                  {isLoading ? (
                    <Tbody>
                      <Tr>
                        <Td colSpan={2} textAlign="center">
                          <Spinner />
                        </Td>
                      </Tr>
                    </Tbody>
                  ) : (
                    <Tbody>
                      {localSuppliers.map((supplier) => (
                        <Tr key={supplier.id} _hover={{ bg: "gray.50" }}>
                          <Td
                            fontWeight={500}
                            borderRight="1px solid"
                            borderColor="gray.200"
                          >
                            {supplier.name}
                          </Td>
                          <Td textAlign="center">
                            <Checkbox
                              isChecked={
                                !supplier.is_line_items_extraction_disabled
                              }
                              onChange={() => handleSelect(supplier.id)}
                            />
                          </Td>
                        </Tr>
                      ))}
                      {(!suppliers ||
                        (suppliers.items && suppliers.items.length === 0)) && (
                        <Tr>
                          <Td colSpan={2} textAlign="center" color="gray.400">
                            No suppliers found
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  )}
                </Table>
              </Box>
              {localSuppliers.length > 0 && (
                <Flex justify="space-between" align="center" mt={4}>
                  <Paginate
                    page={page}
                    data={
                      suppliers ?? { total: 0, limit, offset: 0, items: [] }
                    }
                    handlePageClick={handlePageClick}
                  />
                  <Flex align="center" gap={2}>
                    <Text fontSize={14}>Rows per page</Text>
                    <Select
                      size="sm"
                      value={limit}
                      onChange={(e) => {
                        setLimit(Number(e.target.value));
                        setPage(0);
                      }}
                      w="70px"
                    >
                      {pageSizeOptions.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </Select>
                  </Flex>
                </Flex>
              )}
            </Box>
          )}
        </Box>
        <Flex gap={2}>
          <Text fontWeight={600} color="darkGray">
            {lineItemExtractionEnabled ? "On" : "Off"}
          </Text>
          <Switch
            isChecked={lineItemExtractionEnabled}
            onChange={() =>
              handleLineItemExtractionEnabledChange(!lineItemExtractionEnabled)
            }
            colorScheme="primary"
            size="lg"
            disabled={isCompanyUpdating}
          />
        </Flex>
      </Flex>
    </Box>
  );
};
